"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/lib/cart-context";
import { usePostalCode } from "@/lib/postal-code-context";
import { getProductImageUrl, formatPriceValue } from "@/lib/utils";
import { computeRentalTotalHt } from "@/lib/rental-pricing";
import {
  calculateOrderTotals,
  calculatePromoDiscountHt,
  DEFAULT_VAT_RATE,
} from "@/lib/order-totals";
import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  Heart,
  ShoppingBag,
  Truck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Store,
  MapPin,
  X,
  Ticket,
  Wallet,
  CreditCard,
} from "lucide-react";

interface Favorite {
  product_id: number;
  title: string;
  slug: string;
  image_local: string | null;
  price_ht: number | null;
  display_rent_price_ht: number | null;
  selling_type: string | null;
  store_name: string | null;
  brand: string | null;
}

interface RecommendedProduct {
  id: number;
  title: string;
  slug: string;
  image_local: string | null;
  store_name: string | null;
  display_rent_price_ht: number | null;
  price_ht: number | null;
  selling_type: string | null;
}

export default function PanierPage() {
  const router = useRouter();
  const {
    cart,
    removeFromCart,
    updateQuantity,
    updateRentalDays,
    updateRentalStartDate,
    updateDeliveryMethod,
    cartTotalHt,
    cartInsuranceHt,
  } = useCart();
  const { postalCode: savedPostalCode } = usePostalCode();

  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{
    id: number;
    code: string;
    discount_type: "percentage" | "fixed";
    discount_value: number;
    min_order_ht: number | null;
    eligibleProductIds: number[];
  } | null>(null);
  const [activeTab, setActiveTab] = useState<"panier" | "favoris">("panier");
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [removingFavId, setRemovingFavId] = useState<number | null>(null);
  const [recommended, setRecommended] = useState<RecommendedProduct[]>([]);
  const [recommendedType, setRecommendedType] = useState<"location" | "achat">("location");
  const [scrollPos, setScrollPos] = useState(0);
  const [shippingEstimate, setShippingEstimate] = useState<number | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [itemShippingInfo, setItemShippingInfo] = useState<Record<number, { cost: number | null; available: boolean; freeReason: string | null; basePrice: number | null; deliveryCost: number | null; returnCost: number | null; isRental: boolean }>>({});
  const [availability, setAvailability] = useState<Record<number, {
    available: boolean;
    reason: string | null;
    availableMethods: string[];
  }>>({});

  const getLineTotal = (item: typeof cart[0]) => {
    if (item.sellingType === "RENT" && item.rentalDays) {
      return computeRentalTotalHt(
        item.unitPriceHt,
        item.weekendPriceHt ?? null,
        item.rentalDays,
        item.isWeekendRental,
        item.quantity
      );
    }
    return item.unitPriceHt * item.quantity;
  };

  const TVA_RATE = DEFAULT_VAT_RATE;

  // Promo discount calculation
  const promoDiscountHt = (() => {
    if (!appliedPromo) return 0;
    // Calculate eligible subtotal
    const eligibleItems = cart.filter((item) =>
      appliedPromo.eligibleProductIds.includes(item.productId)
    );
    const eligibleTotalHt = eligibleItems.reduce((sum, item) => sum + getLineTotal(item), 0);

    return calculatePromoDiscountHt({
      cartTotalHt,
      eligibleTotalHt,
      discountType: appliedPromo.discount_type,
      discountValue: appliedPromo.discount_value,
      minOrderHt: appliedPromo.min_order_ht,
    });
  })();

  // Les tarifs de la grille logistique sont enregistrés TTC. On présente leur
  // équivalent HT et on réintègre leur part de TVA dans le récapitulatif.
  const shippingTotalTtc = shippingEstimate ?? 0;
  const {
    taxableTotalHt,
    vat: totalTva,
    totalTtc,
  } = calculateOrderTotals({
    articlesHt: cartTotalHt,
    promoDiscountHt,
    shippingTtc: shippingTotalTtc,
    insuranceWithoutTax: cartInsuranceHt,
  });
  const articleCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const hasRentalItems = cart.some((item) => item.sellingType === "RENT");
  const hasPurchaseItems = cart.some((item) => item.sellingType !== "RENT");
  const [acceptInsurance, setAcceptInsurance] = useState(false);
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const goToCheckout = (mode: "ONLINE" | "ON_RECEPTION") => {
    localStorage.setItem("toolouer_payment_choice", mode);
    router.push("/checkout");
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError("");
    try {
      const res = await fetch("/api/promo-codes/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: promoCode.trim(),
          cartItems: cart.map((item) => ({
            productId: item.productId,
            storeName: item.storeName,
          })),
        }),
      });
      const data = await res.json();
      if (data.valid) {
        // Check min order
        if (data.promoCode.min_order_ht && cartTotalHt < data.promoCode.min_order_ht) {
          setPromoError(`Commande minimum de ${data.promoCode.min_order_ht} € HT requise`);
          return;
        }
        const applied = {
          id: data.promoCode.id,
          code: data.promoCode.code,
          discount_type: data.promoCode.discount_type,
          discount_value: data.promoCode.discount_value,
          min_order_ht: data.promoCode.min_order_ht,
          eligibleProductIds: data.eligibleProductIds,
        };
        setAppliedPromo(applied);
        try { localStorage.setItem("toolouer_applied_promo", JSON.stringify(applied)); } catch {}
        setPromoError("");
      } else {
        setPromoError(data.error || "Code invalide");
        setAppliedPromo(null);
      }
    } catch {
      setPromoError("Erreur de validation");
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoError("");
    try { localStorage.removeItem("toolouer_applied_promo"); } catch {}
  };

  const deliveryMethodLabels: Record<string, string> = {
    AGENCY: "Retrait en magasin",
    DELIVERY: "Livraison à domicile",
    BUILDING_SITE: "Livraison sur chantier",
  };

  // Restaure le code promo persisté (rétrocompat panier ↔ checkout ↔ retour panier)
  useEffect(() => {
    try {
      const stored = localStorage.getItem("toolouer_applied_promo");
      if (stored) setAppliedPromo(JSON.parse(stored));
    } catch {}
  }, []);

  // Check product availability when postal code or cart changes
  useEffect(() => {
    if (!savedPostalCode || savedPostalCode.length < 2 || cart.length === 0) {
      setAvailability({});
      return;
    }

    const abortController = new AbortController();

    fetch("/api/cart/check-availability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: cart.map((item) => ({
          productId: item.productId,
          deliveryMethod: item.deliveryMethod,
        })),
        postalCode: savedPostalCode,
      }),
      signal: abortController.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        if (!abortController.signal.aborted && data.results) {
          setAvailability(data.results);
        }
      })
      .catch(() => {});

    return () => abortController.abort();
  }, [savedPostalCode, cart]);

  const hasUnavailableItems = Object.values(availability).some((a) => !a.available);

  // Calculate shipping estimate when postal code is available
  useEffect(() => {
    if (!savedPostalCode || savedPostalCode.length < 2 || cart.length === 0) {
      setShippingEstimate(null);
      setItemShippingInfo({});
      return;
    }

    const abortController = new AbortController();
    setShippingLoading(true);

    fetch("/api/shipping/calculate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPriceHt: item.unitPriceHt,
          rentalDays: item.rentalDays,
          weekendPriceHt: item.weekendPriceHt,
          isWeekendRental: item.isWeekendRental,
          sellingType: item.sellingType,
          deliveryMethod: item.deliveryMethod,
        })),
        postal_code: savedPostalCode,
      }),
      signal: abortController.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        if (!abortController.signal.aborted) {
          setShippingEstimate(data.totalShipping ?? null);
          const info: Record<number, { cost: number | null; available: boolean; freeReason: string | null; basePrice: number | null; deliveryCost: number | null; returnCost: number | null; isRental: boolean }> = {};
          for (const store of data.stores || []) {
            for (const si of store.items || []) {
              info[si.productId] = {
                cost: si.available === false ? null : (si.shipping?.total ?? 0),
                available: si.available !== false,
                freeReason: si.shipping?.isFree ? (si.shipping?.freeReason || null) : null,
                basePrice: si.shipping?.delivery ?? null,
                deliveryCost: si.shipping?.delivery ?? null,
                returnCost: si.shipping?.return ?? null,
                isRental: si.isRental ?? false,
              };
            }
          }
          setItemShippingInfo(info);
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) setShippingEstimate(null);
      })
      .finally(() => {
        if (!abortController.signal.aborted) setShippingLoading(false);
      });

    return () => abortController.abort();
  }, [savedPostalCode, cart]);

  // Fetch recommended products based on cart content and selected type
  useEffect(() => {
    async function fetchRecommended() {
      try {
        const type = recommendedType === "location" ? "RENT" : "NEW";
        const productIds = cart.map((item) => item.productId).join(",");
        const url = productIds
          ? `/api/cart/recommendations?productIds=${productIds}&type=${type}`
          : `/api/cart/recommendations?type=${type}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setRecommended(data.products || []);
        }
      } catch {
        // ignore
      }
    }
    fetchRecommended();
  }, [cart, recommendedType]);

  // Fetch favorites when tab switches to favoris
  useEffect(() => {
    if (activeTab !== "favoris") return;
    setLoadingFavorites(true);
    fetch("/api/favorites")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.favorites) setFavorites(data.favorites);
      })
      .catch(() => {})
      .finally(() => setLoadingFavorites(false));
  }, [activeTab]);

  const removeFavorite = async (productId: number) => {
    setRemovingFavId(productId);
    try {
      const res = await fetch(`/api/favorites/${productId}`, { method: "DELETE" });
      if (res.ok) {
        setFavorites((prev) => prev.filter((f) => f.product_id !== productId));
      }
    } catch {
      // ignore
    } finally {
      setRemovingFavId(null);
    }
  };

  // Get estimated delivery date (next Tuesday or Friday)
  const getEstimatedDelivery = () => {
    const now = new Date();
    const daysUntilTuesday = (2 - now.getDay() + 7) % 7 || 7;
    const nextDelivery = new Date(now);
    nextDelivery.setDate(now.getDate() + daysUntilTuesday);
    return nextDelivery.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  const scrollContainer = (direction: "left" | "right") => {
    const container = document.getElementById("recommended-scroll");
    if (container) {
      const scrollAmount = 280;
      const newPos = direction === "left" ? scrollPos - scrollAmount : scrollPos + scrollAmount;
      container.scrollTo({ left: newPos, behavior: "smooth" });
      setScrollPos(newPos);
    }
  };

  // Le code n'est affiché comme appliqué que si au moins un article du panier y est éligible
  const eligibleInCart = appliedPromo ? cart.filter((i) => appliedPromo.eligibleProductIds.includes(i.productId)).length : 0;

  return (
    <div className="py-8 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        {/* Tabs: Mon Panier / Mes Favoris */}
        <div className="flex items-center gap-8 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("panier")}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "panier"
                ? "border-secondary text-secondary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <ShoppingCart className="h-4 w-4" />
            Mon Panier
          </button>
          <button
            onClick={() => setActiveTab("favoris")}
            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "favoris"
                ? "border-secondary text-secondary"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <Heart className="h-4 w-4" />
            Mes Favoris
          </button>
        </div>

        {activeTab === "favoris" ? (
          loadingFavorites ? (
            <div className="bg-white rounded-xl border p-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-secondary mx-auto" />
            </div>
          ) : favorites.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center">
              <Heart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Vous n&apos;avez pas encore de favoris.</p>
              <Button asChild variant="outline" className="mt-4">
                <Link href="/categories">Découvrir nos produits</Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {favorites.map((fav) => (
                <div
                  key={fav.product_id}
                  className="bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow group relative"
                >
                  <button
                    onClick={() => removeFavorite(fav.product_id)}
                    disabled={removingFavId === fav.product_id}
                    className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-white/90 border border-gray-200 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Retirer des favoris"
                  >
                    {removingFavId === fav.product_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <Link href={`/produits/${fav.slug}`}>
                    <div className="aspect-square relative bg-white">
                      <Image
                        src={getProductImageUrl(fav.image_local)}
                        alt={fav.title}
                        fill
                        className="object-contain p-4"
                      />
                    </div>
                    <div className="p-3 border-t">
                      <p className="text-xs text-gray-400 uppercase mb-1">
                        {fav.store_name || fav.brand || "TOOLOUER"}
                      </p>
                      <h3 className="text-sm font-medium line-clamp-2 group-hover:text-secondary mb-2">
                        {fav.title}
                      </h3>
                      <p className="text-sm font-bold text-secondary">
                        {fav.selling_type === "RENT"
                          ? `${formatPriceValue(fav.display_rent_price_ht)} € / jour`
                          : fav.price_ht
                            ? `${formatPriceValue(fav.price_ht)} € HT`
                            : "Sur devis"}
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )
        ) : cart.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h1 className="text-2xl font-bold mb-2">Votre panier est vide</h1>
            <p className="text-gray-500 mb-6">
              Parcourez notre catalogue pour trouver le matériel dont vous avez besoin.
            </p>
            <Button
              asChild
              className="bg-secondary hover:bg-secondary/90 text-white"
            >
              <Link href="/categories">
                <ShoppingBag className="mr-2 h-5 w-5" />
                Parcourir le catalogue
              </Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Title */}
            <h1 className="text-lg font-bold mb-6">
              Ma commande ({articleCount})
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart items */}
              <div className="lg:col-span-2 space-y-4">
                {cart.map((item) => (
                  <div
                    key={item.productId}
                    className="bg-white rounded-xl border"
                  >
                    {/* Item header: Vendor */}
                    <div className="px-5 py-3 border-b border-gray-100">
                      <p className="text-sm text-gray-500">
                        Vendu par{" "}
                        <span className="font-medium text-secondary">
                          {item.storeName || "Toolouer"}
                        </span>
                      </p>
                    </div>

                    {/* Item content */}
                    <div className="p-5">
                      <div className="flex gap-4">
                        {/* Image */}
                        <Link
                          href={`/produits/${item.productSlug}`}
                          className="flex-shrink-0"
                        >
                          <div className="relative w-20 h-20 md:w-24 md:h-24 bg-gray-50 rounded-lg overflow-hidden">
                            <Image
                              src={getProductImageUrl(item.productImage)}
                              alt={item.productTitle}
                              fill
                              className="object-contain p-2"
                            />
                          </div>
                        </Link>

                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <Link
                            href={`/produits/${item.productSlug}`}
                            className="font-medium text-sm md:text-base hover:text-secondary line-clamp-2"
                          >
                            {item.productTitle}
                          </Link>

                          {/* Promo badge */}
                          {appliedPromo && appliedPromo.eligibleProductIds.includes(item.productId) && (
                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-green-50 border border-green-200 text-green-700 text-xs rounded-full">
                              <Ticket className="h-3 w-3" />
                              {appliedPromo.code} : {appliedPromo.discount_type === "percentage" ? `-${appliedPromo.discount_value}%` : `-${formatPriceValue(appliedPromo.discount_value)} € HT`}
                            </span>
                          )}

                          {/* Delivery method badge + shipping cost */}
                          {item.deliveryMethod && (
                            <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                              {item.deliveryMethod === "AGENCY" ? (
                                <Store className="h-3.5 w-3.5 text-gray-400" />
                              ) : (
                                <Truck className="h-3.5 w-3.5 text-gray-400" />
                              )}
                              <span className="text-xs text-gray-500">
                                {deliveryMethodLabels[item.deliveryMethod] || item.deliveryMethod}
                              </span>
                              {/* Per-item shipping info */}
                              {item.deliveryMethod !== "AGENCY" && savedPostalCode && itemShippingInfo[item.productId] !== undefined && (
                                <>
                                  <span className="text-xs text-gray-300">·</span>
                                  {!itemShippingInfo[item.productId].available ? (
                                    <span className="text-xs text-amber-600 font-medium">
                                      Non disponible pour {savedPostalCode}
                                    </span>
                                  ) : itemShippingInfo[item.productId].cost && itemShippingInfo[item.productId].cost! > 0 ? (
                                    <span className="text-xs text-gray-500">
                                      {itemShippingInfo[item.productId].isRental && itemShippingInfo[item.productId].returnCost ? (
                                        <>Aller {formatPriceValue(itemShippingInfo[item.productId].deliveryCost! / (1 + TVA_RATE))} € HT + Retour {formatPriceValue(itemShippingInfo[item.productId].returnCost! / (1 + TVA_RATE))} € HT</>
                                      ) : (
                                        <>{formatPriceValue(itemShippingInfo[item.productId].cost! / (1 + TVA_RATE))} € HT</>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-green-600">
                                      Livraison offerte
                                      {itemShippingInfo[item.productId].freeReason && (
                                        <span className="text-gray-400 ml-1">
                                          ({itemShippingInfo[item.productId].freeReason})
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          )}

                          {/* Rental details */}
                          {item.sellingType === "RENT" && (
                            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                              <span>{item.rentalDays} jour{(item.rentalDays || 0) > 1 ? "s" : ""}</span>
                              {item.freeWeekends && (
                                <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded">
                                  week-ends offerts
                                </span>
                              )}
                              {item.isWeekendRental && !item.freeWeekends && (
                                <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                                  forfait week-end
                                </span>
                              )}
                              {item.rentalStartDate && (
                                <span>du {new Date(item.rentalStartDate).toLocaleDateString("fr-FR")}{item.rentalStartTime ? ` ${item.rentalStartTime.replace(":", "h")}` : ""}</span>
                              )}
                              {item.rentalEndDate && (
                                <span>au {new Date(item.rentalEndDate).toLocaleDateString("fr-FR")}{item.rentalEndTime ? ` ${item.rentalEndTime.replace(":", "h")}` : ""}</span>
                              )}
                            </div>
                          )}

                          {/* Price + Quantity + Delete row */}
                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {/* Price */}
                              <span className="text-lg font-bold">
                                {formatPriceValue(getLineTotal(item))} € HT
                              </span>

                              {/* Quantity */}
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() =>
                                    updateQuantity(item.productId, item.quantity - 1)
                                  }
                                  disabled={item.quantity <= 1}
                                  className="h-7 w-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                                <span className="w-8 text-center text-sm font-medium">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    updateQuantity(item.productId, item.quantity + 1)
                                  }
                                  className="h-7 w-7 rounded-full border border-secondary bg-secondary text-white flex items-center justify-center hover:bg-secondary/90"
                                >
                                  <Plus className="h-3 w-3" />
                                </button>
                              </div>
                            </div>

                            {/* Delete */}
                            <button
                              onClick={() => removeFromCart(item.productId)}
                              className="text-gray-400 hover:text-red-500 p-1"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Deposit info */}
                          {item.sellingType === "RENT" && item.depositPrice && (
                            <p className="text-xs text-gray-500 mt-2">
                              Caution : {formatPriceValue(item.depositPrice)} € HT
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Availability warning */}
                    {availability[item.productId] && !availability[item.productId].available && (
                      <div className="px-5 pb-4">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-amber-800 font-medium">
                                {availability[item.productId].reason}
                              </p>
                              {availability[item.productId].availableMethods.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-amber-700 mb-1.5">
                                    Modes disponibles pour {savedPostalCode} :
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {availability[item.productId].availableMethods.map((method) => (
                                      <button
                                        key={method}
                                        onClick={() => updateDeliveryMethod(item.productId, method)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors"
                                      >
                                        {method === "AGENCY" ? (
                                          <Store className="h-3 w-3" />
                                        ) : (
                                          <Truck className="h-3 w-3" />
                                        )}
                                        {deliveryMethodLabels[method] || method}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {availability[item.productId].availableMethods.length === 0 && (
                                <button
                                  onClick={() => removeFromCart(item.productId)}
                                  className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                                >
                                  Retirer du panier
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Promo code */}
                <div className="mt-2 space-y-2">
                  {appliedPromo && eligibleInCart > 0 ? (
                    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">
                          Code <span className="font-mono font-bold">{appliedPromo.code}</span> appliqué
                        </p>
                        <p className="text-xs text-green-600">
                          {appliedPromo.discount_type === "percentage"
                            ? `-${appliedPromo.discount_value}%`
                            : `-${formatPriceValue(appliedPromo.discount_value)} € HT`}
                          {eligibleInCart < cart.length
                            ? ` sur ${eligibleInCart} article${eligibleInCart > 1 ? "s" : ""}`
                            : " sur tout le panier"}
                        </p>
                      </div>
                      <button
                        onClick={removePromo}
                        className="text-green-600 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex">
                      <span className="col-span-2 text-base font-bold text-gray-800 whitespace-nowrap sm:text-sm sm:font-normal sm:text-gray-600">Un code promotion ?</span>
                      <Input
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          setPromoError("");
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleValidatePromo()}
                        placeholder="Saisir votre code"
                        className={`min-w-0 max-w-none h-11 text-base font-semibold sm:max-w-xs sm:h-9 sm:text-sm sm:font-normal ${promoError ? "border-red-300" : ""}`}
                      />
                      <Button
                        size="sm"
                        onClick={handleValidatePromo}
                        disabled={promoLoading || !promoCode.trim()}
                        className="bg-secondary hover:bg-secondary/90 text-white px-6"
                      >
                        {promoLoading ? "..." : "Valider"}
                      </Button>
                    </div>
                  )}
                  {promoError && (
                    <p className="text-xs text-red-500 ml-1">{promoError}</p>
                  )}
                </div>
              </div>

              {/* Summary sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl border p-6 sticky top-24">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Total des articles ({articleCount})
                      </span>
                      <span className="font-medium">
                        {formatPriceValue(cartTotalHt)} € HT
                      </span>
                    </div>
                    {(() => {
                      const infos = Object.values(itemShippingInfo);
                      // Calculate totals per item * quantity from cart
                      let totalDelivery = 0;
                      let totalReturn = 0;
                      for (const item of cart) {
                        const si = itemShippingInfo[item.productId];
                        if (!si || !si.available || si.cost === 0 || si.cost === null) continue;
                        totalDelivery += (si.deliveryCost || 0) * item.quantity;
                        if (si.isRental) {
                          totalReturn += (si.returnCost || 0) * item.quantity;
                        }
                      }
                      const hasReturn = totalReturn > 0;
                      return (
                        <div>
                          {hasReturn && !shippingLoading && shippingEstimate !== null && shippingEstimate > 0 ? (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Livraison aller</span>
                                <span className="font-medium">{formatPriceValue(totalDelivery / (1 + TVA_RATE))}&nbsp;€ HT</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Livraison retour</span>
                                <span className="font-medium">{formatPriceValue(totalReturn / (1 + TVA_RATE))}&nbsp;€ HT</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Livraison estimée</span>
                              <span className="font-medium">
                                {shippingLoading ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
                                ) : shippingEstimate !== null ? (
                                  infos.some((i) => !i.available) ? (
                                    <span className="text-xs text-amber-600">Partiellement non disponible pour {savedPostalCode}</span>
                                  ) : shippingEstimate === 0 ? (
                                    <span className="text-green-600">
                                      {(() => {
                                        const totalBase = infos.reduce((sum, i) => sum + (i.basePrice || 0), 0);
                                        return totalBase > 0 ? (
                                          <>
                                            <span className="line-through text-gray-400 mr-1">{formatPriceValue(totalBase / (1 + TVA_RATE))} € HT</span>
                                            Offerte
                                          </>
                                        ) : "Gratuit";
                                      })()}
                                    </span>
                                  ) : `${formatPriceValue(shippingEstimate / (1 + TVA_RATE))} € HT`
                                ) : (
                                  <span className="text-xs text-gray-400">Renseignez votre CP</span>
                                )}
                              </span>
                            </div>
                          )}
                          {/* Free shipping reason detail */}
                          {!shippingLoading && shippingEstimate === 0 && infos.some((i) => i.freeReason) && (
                            <p className="text-xs text-green-600 mt-1 text-right">
                              {[...new Set(infos.map((i) => i.freeReason).filter(Boolean))].join(" · ")}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    {/* Insurance line - only for rental items */}
                    {hasRentalItems && cartInsuranceHt > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Assurance (sans TVA)</span>
                        <span className="font-medium">
                          {formatPriceValue(cartInsuranceHt)} €
                        </span>
                      </div>
                    )}
                    {promoDiscountHt > 0 && appliedPromo && (
                      <div className="flex justify-between text-green-600">
                        <span>
                          Remise ({appliedPromo.code})
                        </span>
                        <span className="font-medium">
                          -{formatPriceValue(promoDiscountHt)} € HT
                        </span>
                      </div>
                    )}
                    <div className="border-t pt-3 flex justify-between">
                      <span className="text-gray-600">Sous-total taxable HT</span>
                      <span className="font-medium">
                        {formatPriceValue(taxableTotalHt)} €
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">TVA (20 %)</span>
                      <span className="font-medium">{formatPriceValue(totalTva)} €</span>
                    </div>
                    <div className="flex justify-between text-base">
                      <span className="font-bold">Total TTC</span>
                      <span className="font-bold">
                        {formatPriceValue(totalTtc)} €
                      </span>
                    </div>
                  </div>


                  {/* Conditions checkboxes */}
                  {hasRentalItems && (
                    <div className="mt-4 space-y-2">
                      <label className="flex items-start gap-2.5 cursor-pointer bg-gray-50 rounded-lg p-3">
                        <input
                          type="checkbox"
                          checked={acceptInsurance}
                          onChange={(e) => setAcceptInsurance(e.target.checked)}
                          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-secondary focus:ring-secondary"
                        />
                        <span className="text-xs text-gray-600 leading-relaxed">
                          J&apos;accepte l&apos;assurance obligatoire de {formatPriceValue(cartInsuranceHt)} € , j&apos;ai lu et j&apos;accepte{" "}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setShowInsuranceModal(true); }}
                            className="text-secondary underline hover:text-secondary/80"
                          >
                            les conditions d&apos;assurance.
                          </button>
                        </span>
                      </label>

                      <label className="flex items-start gap-2.5 cursor-pointer bg-gray-50 rounded-lg p-3">
                        <input
                          type="checkbox"
                          checked={acceptConditions}
                          onChange={(e) => setAcceptConditions(e.target.checked)}
                          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-secondary focus:ring-secondary"
                        />
                        <span className="text-xs text-gray-600 leading-relaxed">
                          J&apos;accepte{" "}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setShowConditionsModal(true); }}
                            className="text-secondary underline hover:text-secondary/80"
                          >
                            les conditions générales de location.
                          </button>
                        </span>
                      </label>
                    </div>
                  )}

                  <div className="mt-4">
                    {(() => {
                      const disabled = hasRentalItems && (!acceptInsurance || !acceptConditions);
                      const purchaseCount = cart.filter((i) => i.sellingType !== "RENT").reduce((s, i) => s + i.quantity, 0);

                      if (hasRentalItems) {
                        return (
                          <>
                            <div className="grid grid-cols-1 gap-2">
                              <Button
                                className="w-full bg-secondary hover:bg-secondary/90 text-white py-5 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-0.5 h-auto"
                                onClick={() => !hasPurchaseItems && goToCheckout("ON_RECEPTION")}
                                disabled={disabled || hasPurchaseItems}
                              >
                                <span className="flex items-center gap-2 font-semibold">
                                  <Wallet className="h-4 w-4" />
                                  Pré-réserver (paiement à la réception)
                                </span>
                                {hasPurchaseItems && (
                                  <span className="text-[11px] font-normal text-amber-100">
                                    {purchaseCount} article{purchaseCount > 1 ? "s" : ""} non compatible{purchaseCount > 1 ? "s" : ""}
                                  </span>
                                )}
                              </Button>
                              <Button
                                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-5 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold h-auto"
                                onClick={() => goToCheckout("ONLINE")}
                                disabled={disabled}
                              >
                                <CreditCard className="h-4 w-4" />
                                Payer maintenant
                              </Button>
                            </div>
                            {disabled && (
                              <p className="text-xs text-amber-600 text-center mt-2">
                                Veuillez accepter les conditions pour continuer
                              </p>
                            )}
                          </>
                        );
                      }

                      return (
                        <Button
                          className="w-full bg-secondary hover:bg-secondary/90 text-white py-6"
                          onClick={() => goToCheckout("ONLINE")}
                        >
                          Valider le panier
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Recommended products section */}
            {recommended.length > 0 && (
              <div className="mt-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold">Nos clients ont adoré</h2>
                  <div className="flex items-center gap-3">
                    {/* Location / Achat toggle */}
                    <div className="flex rounded-full border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => setRecommendedType("location")}
                        className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                          recommendedType === "location"
                            ? "bg-secondary text-white"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        Location
                      </button>
                      <button
                        onClick={() => setRecommendedType("achat")}
                        className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                          recommendedType === "achat"
                            ? "bg-secondary text-white"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        Achat
                      </button>
                    </div>
                    {/* Scroll arrows */}
                    <div className="flex gap-1">
                      <button
                        onClick={() => scrollContainer("left")}
                        className="h-8 w-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => scrollContainer("right")}
                        className="h-8 w-8 rounded-full border border-secondary bg-secondary text-white flex items-center justify-center hover:bg-secondary/90"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  id="recommended-scroll"
                  className="flex gap-4 overflow-x-auto scrollbar-hide pb-4"
                >
                  {recommended.map((product) => (
                    <Link
                      key={product.id}
                      href={`/produits/${product.slug}`}
                      className="flex-shrink-0 w-[200px] bg-white rounded-xl border hover:shadow-md transition-shadow group"
                    >
                      <div className="aspect-square relative bg-white rounded-t-xl overflow-hidden">
                        <Image
                          src={getProductImageUrl(product.image_local)}
                          alt={product.title}
                          fill
                          className="object-contain p-4"
                        />
                      </div>
                      <div className="p-3 border-t">
                        <p className="text-xs text-gray-400 uppercase mb-1">
                          {product.store_name || "TOOLOUER"}
                        </p>
                        <h3 className="text-sm font-medium line-clamp-2 group-hover:text-secondary mb-2">
                          {product.title}
                        </h3>
                        <p className="text-sm font-bold text-secondary">
                          {product.selling_type === "RENT"
                            ? `${formatPriceValue(product.display_rent_price_ht)} € / jour`
                            : `${formatPriceValue(product.price_ht)} €`}
                        </p>
                        <Button
                          size="sm"
                          className="w-full mt-2 bg-secondary/10 text-secondary hover:bg-secondary hover:text-white text-xs"
                        >
                          {product.selling_type === "RENT"
                            ? "Réserver ma location"
                            : "Ajouter au panier"}
                        </Button>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* Insurance conditions modal */}
      {showInsuranceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowInsuranceModal(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h2 className="text-lg font-bold mb-4">Conditions d&apos;assurance</h2>
              <div className="text-sm text-gray-600 space-y-3">
                <p>L&apos;assurance couvre les dommages accidentels survenus pendant la période de location du matériel.</p>
                <p><strong>Couverture :</strong> L&apos;assurance prend en charge les réparations ou le remplacement du matériel en cas de dommage accidentel survenu dans le cadre d&apos;une utilisation normale.</p>
                <p><strong>Exclusions :</strong> Ne sont pas couverts : le vol, la perte, les dommages intentionnels, l&apos;usure normale, et les dommages résultant d&apos;une utilisation non conforme aux instructions du fabricant.</p>
                <p><strong>Montant :</strong> L&apos;assurance est calculée à hauteur de 10% du montant HT de la location.</p>
                <p><strong>Déclaration :</strong> Tout sinistre doit être déclaré dans les 48 heures suivant sa survenance.</p>

              </div>
              <button
                onClick={() => setShowInsuranceModal(false)}
                className="mt-6 w-full bg-secondary text-white py-2.5 rounded-lg font-medium hover:bg-secondary/90"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rental conditions modal */}
      {showConditionsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowConditionsModal(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h2 className="text-lg font-bold mb-4">Conditions générales de location</h2>
              <div className="text-sm text-gray-600 space-y-3">
                <p><strong>Article 1 - Objet :</strong> Les présentes conditions générales régissent la location de matériel professionnel via la plateforme Toolouer.</p>
                <p><strong>Article 2 - Durée :</strong> La durée de location est définie lors de la commande. Toute prolongation doit faire l&apos;objet d&apos;un accord préalable.</p>
                <p><strong>Article 3 - Obligations du locataire :</strong> Le locataire s&apos;engage à utiliser le matériel conformément à sa destination, à l&apos;entretenir avec soin et à le restituer en bon état de fonctionnement.</p>
                <p><strong>Article 4 - Caution :</strong> Une caution peut être demandée au moment de la prise en charge du matériel. Elle sera restituée après vérification de l&apos;état du matériel.</p>
                <p><strong>Article 5 - Responsabilité :</strong> Le locataire est responsable du matériel pendant toute la durée de la location, y compris en cas de vol ou de détérioration.</p>
                <p><strong>Article 6 - Restitution :</strong> Le matériel doit être restitué à la date convenue, propre et en bon état. Tout retard pourra donner lieu à des pénalités.</p>

              </div>
              <button
                onClick={() => setShowConditionsModal(false)}
                className="mt-6 w-full bg-secondary text-white py-2.5 rounded-lg font-medium hover:bg-secondary/90"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
